import { useState, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGo } from '@/lib/useGo';
import { Icon, Card, Pill } from '@/components/ds';
import { CodeBlock, Method } from '@/components/code';
import type { Go } from '@/lib/types';

// ---- Types -----------------------------------------------------------------
interface FlowNodeDef {
  tone: 'payer' | 'recipient' | 'platform' | 'wallet' | 'bank';
  icon: string;
  label: string;
  sub?: string;
}
interface FlowEdge {
  from: [number, number];
  to: [number, number];
  tone?: 'neutral' | 'accent' | 'brand';
  manage?: boolean;
  label?: string;
}
interface FlowConfig {
  columns: FlowNodeDef[][];
  edges: FlowEdge[];
  height: number;
  wrap?: { col: number; label: string };
}
interface UseCase {
  id: string;
  icon: string;
  title: string;
  tagline: string;
  summary: string;
  tags: { kind: string; text: string }[];
  fromLabel?: string;
  toLabel?: string;
  flow: FlowConfig;
  cardFlow?: FlowConfig;
  steps: { method: string; path: string; title: string; desc: string }[];
  goodFor: string[];
  code: Record<string, string>;
  response: string;
}

// ---- FlowDiagram -----------------------------------------------------------
const TONES: Record<string, { bg: string; border: string; icon: string; fg: string; iconBg?: string }> = {
  platform: { bg: 'var(--accent)', border: 'var(--accent-hover)', icon: '#fff', fg: '#fff', iconBg: 'rgba(255,255,255,.16)' },
  payer: { bg: 'var(--accent-soft)', border: 'var(--accent)', icon: 'var(--accent)', fg: 'var(--accent-text)' },
  recipient: { bg: '#fff', border: 'var(--neutral-500)', icon: 'var(--neutral-800)', fg: 'var(--text-primary)' },
  wallet: {
    bg: 'linear-gradient(135deg,#2a1a44,#16171f)',
    border: '#4a3570',
    icon: '#cbb6ff',
    fg: '#fff',
    iconBg: 'rgba(203,182,255,.16)',
  },
  bank: { bg: '#fff', border: 'var(--neutral-500)', icon: 'var(--neutral-800)', fg: 'var(--text-primary)' },
};
const EDGE_TONES: Record<string, { line: string; dash: string; op: number }> = {
  neutral: { line: 'var(--neutral-500)', dash: 'var(--brand-600)', op: 0.5 },
  accent: { line: 'var(--accent)', dash: 'var(--accent)', op: 0.32 },
  brand: { line: 'var(--brand-500)', dash: 'var(--brand-600)', op: 0.55 },
};

interface FlowNodeProps {
  node: FlowNodeDef;
  x: number;
  y: number;
  w: number;
  h: number;
  compact?: boolean;
  noIcon?: boolean;
}
function FlowNode({ node, x, y, w, h, compact = false, noIcon = false }: FlowNodeProps) {
  const t = TONES[node.tone] || TONES.recipient;
  return (
    <div
      style={{
        position: 'absolute',
        left: x - w / 2,
        top: y - h / 2,
        width: w,
        height: h,
        background: t.bg,
        border: `1.5px solid ${t.border}`,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 10,
        padding: noIcon ? '0 12px' : compact ? '0 10px' : '0 13px',
        boxShadow: 'var(--shadow-400)',
        zIndex: 2,
        overflow: 'hidden',
      }}
    >
      {!noIcon && (
        <span
          style={{
            width: compact ? 24 : 32,
            height: compact ? 24 : 32,
            borderRadius: 8,
            flexShrink: 0,
            background: t.iconBg || 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: t.iconBg ? 'none' : `1px solid ${t.border}`,
          }}
        >
          <Icon name={node.icon} size={compact ? 14 : 18} color={t.icon} />
        </span>
      )}
      <div style={{ minWidth: 0, lineHeight: 1.2 }}>
        <div
          style={{
            fontSize: compact ? 12 : 13.5,
            fontWeight: 600,
            color: t.fg,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {node.label}
        </div>
        {!compact && node.sub && (
          <div
            className="om-mono"
            style={{
              fontSize: 11,
              color: t.fg === '#fff' ? '#c9bff0' : 'var(--text-secondary)',
              marginTop: 2,
              whiteSpace: 'nowrap',
            }}
          >
            {node.sub}
          </div>
        )}
      </div>
    </div>
  );
}

interface FlowDiagramProps {
  columns: FlowNodeDef[][];
  edges: FlowEdge[];
  height?: number;
  compact?: boolean;
  wrap?: { col: number; label: string } | null;
}
function FlowDiagram({ columns, edges, height = 170, compact = false, wrap = null }: FlowDiagramProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => setW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cols = columns.length;
  const noIcon = compact && cols >= 3;
  const nodeW = compact ? (cols >= 3 ? 144 : 168) : cols >= 3 ? 184 : 188;
  const nodeH = compact ? 44 : 56;
  const padX = compact ? 6 : 12;

  const xFirst = nodeW / 2 + padX;
  const xLast = w - nodeW / 2 - padX;
  const xs = cols === 1 ? [w / 2] : columns.map((_, c) => xFirst + (xLast - xFirst) * (c / (cols - 1)));

  const colY = (count: number, i: number): number => {
    const topRes = wrap ? (compact ? 16 : 18) : 0;
    const usable = height - topRes;
    const gap = Math.min(nodeH + (compact ? 10 : 20), (usable - nodeH) / Math.max(count - 1, 1));
    return topRes + usable / 2 - ((count - 1) * gap) / 2 + i * gap;
  };

  const pos = columns.map((nodes, c) => nodes.map((n, i) => ({ n, x: xs[c], y: colY(nodes.length, i) })));

  interface BuiltEdge {
    idx: number;
    d: string;
    mx: number;
    my: number;
    tone: string;
    manage: boolean;
    label?: string;
  }
  const built: BuiltEdge[] = (edges || [])
    .map((e, idx) => {
      const a = pos[e.from[0]] && pos[e.from[0]][e.from[1]];
      const b = pos[e.to[0]] && pos[e.to[0]][e.to[1]];
      if (!a || !b) return null;
      const x1 = a.x + nodeW / 2,
        y1 = a.y,
        x2 = b.x - nodeW / 2,
        y2 = b.y;
      const dx = Math.max((x2 - x1) * 0.5, 26);
      const d = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
      return { idx, d, mx: (x1 + x2) / 2, my: (y1 + y2) / 2, tone: e.tone || 'neutral', manage: !!e.manage, label: e.label };
    })
    .filter(Boolean) as BuiltEdge[];

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', height }}>
      {w > 0 && (
        <svg width={w} height={height} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
          <defs>
            {Object.keys(EDGE_TONES).map((k) => (
              <marker key={k} id={`ar-${k}`} markerWidth="8" markerHeight="8" refX="6.5" refY="4" orient="auto">
                <path
                  d="M1,1 L7,4 L1,7"
                  fill="none"
                  stroke={EDGE_TONES[k].line}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </marker>
            ))}
          </defs>
          {built.map((e) => {
            const et = EDGE_TONES[e.tone] || EDGE_TONES.neutral;
            if (e.manage) {
              return (
                <path
                  key={e.idx}
                  d={e.d}
                  fill="none"
                  stroke={et.line}
                  strokeOpacity={0.7}
                  strokeWidth="1.6"
                  strokeDasharray="2 5"
                  markerEnd={`url(#ar-${e.tone})`}
                />
              );
            }
            return (
              <g key={e.idx}>
                <path d={e.d} fill="none" stroke={et.line} strokeOpacity={et.op} strokeWidth="2" markerEnd={`url(#ar-${e.tone})`} />
                <path
                  className="flow-dash"
                  d={e.d}
                  fill="none"
                  stroke={et.dash}
                  strokeWidth="2"
                  strokeDasharray="3 9"
                  strokeLinecap="round"
                />
              </g>
            );
          })}
        </svg>
      )}
      {w > 0 &&
        !compact &&
        built
          .filter((e) => e.label)
          .map((e) => (
            <div
              key={'lbl' + e.idx}
              style={{
                position: 'absolute',
                left: e.mx,
                top: e.my,
                transform: 'translate(-50%,-50%)',
                zIndex: 3,
                background: '#fff',
                border: '1px solid var(--border-static)',
                borderRadius: 99,
                padding: '2px 9px',
                fontSize: 10.5,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                letterSpacing: '.02em',
                whiteSpace: 'nowrap',
                boxShadow: 'var(--shadow-400)',
              }}
            >
              {e.label}
            </div>
          ))}
      {wrap &&
        w > 0 &&
        pos[wrap.col] &&
        pos[wrap.col].length > 0 &&
        (() => {
          const col = pos[wrap.col];
          const top = col[0].y - nodeH / 2 - 12;
          const bottom = col[col.length - 1].y + nodeH / 2 + 12;
          return (
            <div
              style={{
                position: 'absolute',
                left: xs[wrap.col] - nodeW / 2 - 12,
                top,
                width: nodeW + 24,
                height: bottom - top,
                border: '1.5px dashed var(--brand-400)',
                borderRadius: 14,
                background: 'rgba(120,73,255,0.045)',
                zIndex: 1,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 14,
                  transform: 'translateY(-50%)',
                  background: '#fff',
                  padding: '2px 9px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--brand-800)',
                  border: '1px solid var(--brand-200)',
                  borderRadius: 99,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {wrap.label}
              </span>
            </div>
          );
        })()}
      {pos.map((nodes, c) =>
        nodes.map((p, i) => <FlowNode key={`${c}-${i}`} node={p.n} x={p.x} y={p.y} w={nodeW} h={nodeH} compact={compact} noIcon={noIcon} />)
      )}
    </div>
  );
}

// ---- Use case data ---------------------------------------------------------

const MASS_CODE = {
  curl: `# Repeat one POST per recipient. A unique idempotencyKey per row
# makes the whole batch safe to retry without double-paying.
curl https://api.melio.com/v2/payments \\
  -H "api-key: melio_secret_..." \\
  -H "Melio-Entity-Id: ent_acme_ins" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: claim_1041" \\
  -X POST \\
  -d '{
    "amount": 480000,
    "originatingAccountId": "acct_9a2f7",
    "receivingAccountId": "acct_7c4b1",
    "deductionDate": "2026-07-01"
  }'`,
  node: `import { Melio } from "@melio/sdk";

const melio = new Melio(process.env.MELIO_API_KEY);

// One payer, one funding source — a payment per recipient.
const batch = [
  { receivingAccountId: "acct_7c4b1", amount: 480000, ref: "inv_1041" },
  { receivingAccountId: "acct_5p2q9", amount: 125000, ref: "inv_1042" },
  { receivingAccountId: "acct_3a8w2", amount:  92000, ref: "inv_1043" },
];

const payments = await Promise.all(
  batch.map((row) =>
    melio.payments.create("ent_9688713", {
      amount: row.amount,
      originatingAccountId: "acct_9a2f7",
      receivingAccountId: row.receivingAccountId,
      deductionDate: "2026-06-10",
      idempotencyKey: row.ref, // per-row key → replay-safe batch
    })
  )
);

console.log(\`scheduled \${payments.length} payments\`);`,
  python: `import os
from melio import Melio

melio = Melio(os.environ["MELIO_API_KEY"])

# One payer, one funding source — a payment per recipient.
batch = [
    {"receiving_account_id": "acct_7c4b1", "amount": 480000, "ref": "inv_1041"},
    {"receiving_account_id": "acct_5p2q9", "amount": 125000, "ref": "inv_1042"},
    {"receiving_account_id": "acct_3a8w2", "amount":  92000, "ref": "inv_1043"},
]

payments = [
    melio.payments.create(
        "ent_9688713",
        amount=row["amount"],
        originating_account_id="acct_9a2f7",
        receiving_account_id=row["receiving_account_id"],
        deduction_date="2026-06-10",
        idempotency_key=row["ref"],
    )
    for row in batch
]

print(f"scheduled {len(payments)} payments")`,
  go: `package main

import (
\t"context"
\t"fmt"

\tmelio "github.com/melio/melio-go"
)

func main() {
\tclient := melio.New("melio_secret_...")

\tbatch := []struct {
\t\tDM     string
\t\tAmount int
\t\tRef    string
\t}{
\t\t{"acct_7c4b1", 480000, "inv_1041"},
\t\t{"acct_5p2q9", 125000, "inv_1042"},
\t\t{"acct_3a8w2", 92000, "inv_1043"},
\t}

\tfor _, row := range batch {
\t\t_, err := client.Payments.Create(context.Background(), "ent_9688713", &melio.PaymentParams{
\t\t\tAmount:           melio.Int(row.Amount),
\t\t\tOriginatingAccountID:  melio.String("acct_9a2f7"),
\t\t\tReceivingAccountID: melio.String(row.DM),
\t\t\tDeductionDate:    melio.String("2026-06-10"),
\t\t\tIdempotencyKey:   melio.String(row.Ref),
\t\t})
\t\tif err != nil {
\t\t\tpanic(err)
\t\t}
\t}
\tfmt.Printf("scheduled %d payments\\n", len(batch))
}`,
};

const M2M_CODE = {
  curl: `# 1) Resolve the paying business's funding source (internal account)
curl "https://api.melio.com/v2/accounts?ownershipType=internal" \\
  -H "api-key: melio_secret_..." \\
  -H "Melio-Entity-Id: ent_2k9p4x"

# 2) Create the payout from that business to its recipient
curl https://api.melio.com/v2/payments \\
  -H "api-key: melio_secret_..." \\
  -H "Melio-Entity-Id: ent_2k9p4x" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: ent_2k9p4x:inv_88" \\
  -X POST \\
  -d '{
    "amount": 920000,
    "originatingAccountId": "acct_cedar1",
    "receivingAccountId": "acct_5p2q9",
    "deductionDate": "2026-06-10"
  }'`,
  node: `import { Melio } from "@melio/sdk";

const melio = new Melio(process.env.MELIO_API_KEY); // trusted partner key

// A marketplace settling many businesses to many recipients.
for (const job of disbursements) {
  // each business pays from its own verified funding source
  const sources = await melio.accounts.list(job.businessId, { ownershipType: "internal" });
  const originatingAccountId = sources.data[0].id;

  await Promise.all(
    job.payouts.map((p) =>
      melio.payments.create(job.businessId, {
        amount: p.amount,
        originatingAccountId,
        receivingAccountId: p.receivingAccountId,
        deductionDate: job.date,
        idempotencyKey: \`\${job.businessId}:\${p.ref}\`,
      })
    )
  );
}`,
  python: `import os
from melio import Melio

melio = Melio(os.environ["MELIO_API_KEY"])  # trusted partner key

# A marketplace settling many businesses to many recipients.
for job in disbursements:
    sources = melio.funding_sources.list(job["business_id"])
    originating_account_id = sources.data[0].id

    for p in job["payouts"]:
        melio.payments.create(
            job["business_id"],
            amount=p["amount"],
            originating_account_id=originating_account_id,
            receiving_account_id=p["receiving_account_id"],
            deduction_date=job["date"],
            idempotency_key=f"{job['business_id']}:{p['ref']}",
        )`,
  go: `package main

import (
\t"context"
\t"fmt"

\tmelio "github.com/melio/melio-go"
)

func main() {
\tclient := melio.New("melio_secret_...") // trusted partner key

\tfor _, job := range disbursements {
\t\tsources, err := client.Accounts.List(context.Background(), job.BusinessID, &melio.AccountListParams{OwnershipType: melio.String("internal")})
\t\tif err != nil {
\t\t\tpanic(err)
\t\t}
\t\toriginatingAccountID := sources.Data[0].ID

\t\tfor _, p := range job.Payouts {
\t\t\t_, err := client.Payments.Create(context.Background(), job.BusinessID, &melio.PaymentParams{
\t\t\t\tAmount:           melio.Int(p.Amount),
\t\t\t\tOriginatingAccountID:  melio.String(originatingAccountID),
\t\t\t\tReceivingAccountID: melio.String(p.ReceivingAccountID),
\t\t\t\tDeductionDate:    melio.String(job.Date),
\t\t\t\tIdempotencyKey:   melio.String(fmt.Sprintf("%s:%s", job.BusinessID, p.Ref)),
\t\t\t})
\t\t\tif err != nil {
\t\t\t\tpanic(err)
\t\t\t}
\t\t}
\t}
}`,
};

const RECURRING_CODE = {
  curl: `# Called monthly by your scheduler. Period-scoped idempotency key
# ensures a retried run never double-pays anyone.
curl https://api.melio.com/v2/payments \\
  -H "api-key: melio_secret_..." \\
  -H "Melio-Entity-Id: ent_9688713" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 2026-07:rent" \\
  -X POST \\
  -d '{
    "amount": 125000,
    "originatingAccountId": "acct_9a2f7",
    "receivingAccountId": "acct_7c4b1",
    "deductionDate": "2026-07-01",
    "deliveryPreference": "express-check"
  }'`,
  node: `import { Melio } from "@melio/sdk";

const melio = new Melio(process.env.MELIO_API_KEY);

// Called monthly by your scheduler (cron, queue, etc.).
// The period-scoped idempotency key makes retries safe.
const period = "2026-07";
const schedule = [
  { receivingAccountId: "acct_7c4b1", amount: 125000, ref: "rent",  pref: "standard-check" },
  { receivingAccountId: "acct_5p2q9", amount:  92000, ref: "linen", pref: "virtual-card" },
  { receivingAccountId: "acct_3a8w2", amount:  54200, ref: "pkg",   pref: "standard-ach" },
];

await Promise.all(
  schedule.map((row) =>
    melio.payments.create("ent_9688713", {
      amount: row.amount,
      originatingAccountId: "acct_9a2f7",
      receivingAccountId: row.receivingAccountId,
      deductionDate: \`\${period}-01\`,
      deliveryPreference: row.pref,
      idempotencyKey: \`\${period}:\${row.ref}\`,
    })
  )
);`,
  python: `import os
from melio import Melio

melio = Melio(os.environ["MELIO_API_KEY"])

# Called monthly by your scheduler.
period = "2026-07"
schedule = [
    {"receiving_account_id": "acct_7c4b1", "amount": 125000, "ref": "rent",  "pref": "standard-check"},
    {"receiving_account_id": "acct_5p2q9", "amount":  92000, "ref": "linen", "pref": "virtual-card"},
    {"receiving_account_id": "acct_3a8w2", "amount":  54200, "ref": "pkg",   "pref": "standard-ach"},
]

for row in schedule:
    melio.payments.create(
        "ent_9688713",
        amount=row["amount"],
        originating_account_id="acct_9a2f7",
        receiving_account_id=row["receiving_account_id"],
        deduction_date=f"{period}-01",
        delivery_preference=row["pref"],
        idempotency_key=f"{period}:{row['ref']}",
    )`,
  go: `package main

import (
\t"context"
\t"fmt"

\tmelio "github.com/melio/melio-go"
)

func main() {
\tclient := melio.New("melio_secret_...")

\tperiod := "2026-07"
\tschedule := []struct {
\t\tDM   string
\t\tAmt  int
\t\tRef  string
\t\tPref string
\t}{
\t\t{"acct_7c4b1", 125000, "rent",  "check"},
\t\t{"acct_5p2q9",  92000, "linen", "card" },
\t\t{"acct_3a8w2",  54200, "pkg",   "ach"  },
\t}

\tfor _, row := range schedule {
\t\t_, err := client.Payments.Create(context.Background(), "ent_9688713", &melio.PaymentParams{
\t\t\tAmount:              melio.Int(row.Amt),
\t\t\tOriginatingAccountID:     melio.String("acct_9a2f7"),
\t\t\tReceivingAccountID:    melio.String(row.DM),
\t\t\tDeductionDate:       melio.String(period + "-01"),
\t\t\tDeliveryPreference:  melio.String(row.Pref),
\t\t\tIdempotencyKey:      melio.String(fmt.Sprintf("%s:%s", period, row.Ref)),
\t\t})
\t\tif err != nil {
\t\t\tpanic(err)
\t\t}
\t}
}`,
};

const AGENT_CODE = {
  curl: `# An AI agent calls the same endpoint your app uses.
# Guardrails (approved vendors, spend cap) live in your code.
curl https://api.melio.com/v2/payments \\
  -H "api-key: melio_secret_..." \\
  -H "Melio-Entity-Id: ent_9688713" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: agent_task_8f2a" \\
  -X POST \\
  -d '{
    "amount": 125000,
    "originatingAccountId": "acct_9a2f7",
    "receivingAccountId": "acct_7c4b1",
    "deductionDate": "2026-07-10",
    "noteToRecipient": "Flour order — approved by AP agent"
  }'`,
  node: `import { Melio } from "@melio/sdk";

const melio = new Melio(process.env.MELIO_API_KEY);

// Guardrails enforced before the agent can pay.
const APPROVED_VENDORS = new Set(["acct_7c4b1", "acct_5p2q9"]);
const SPEND_CAP = 500_000; // $5,000 per payment

async function agentPay(decision: {
  businessId: string;
  vendorId: string;
  amount: number;
  date: string;
  taskId: string;
  memo: string;
}) {
  if (!APPROVED_VENDORS.has(decision.vendorId)) throw new Error("Vendor not approved");
  if (decision.amount > SPEND_CAP) throw new Error("Exceeds spend cap");

  return melio.payments.create(decision.businessId, {
    amount: decision.amount,
    originatingAccountId: "acct_9a2f7",
    receivingAccountId: decision.vendorId,
    deductionDate: decision.date,
    idempotencyKey: decision.taskId,
    note: decision.memo,
  });
}`,
  python: `import os
from melio import Melio

melio = Melio(os.environ["MELIO_API_KEY"])

APPROVED_VENDORS = {"acct_7c4b1", "acct_5p2q9"}
SPEND_CAP = 500_000  # $5,000 per payment

def agent_pay(decision: dict):
    if decision["vendor_id"] not in APPROVED_VENDORS:
        raise ValueError("Vendor not approved")
    if decision["amount"] > SPEND_CAP:
        raise ValueError("Exceeds spend cap")

    return melio.payments.create(
        decision["business_id"],
        amount=decision["amount"],
        originating_account_id="acct_9a2f7",
        receiving_account_id=decision["vendor_id"],
        deduction_date=decision["date"],
        idempotency_key=decision["task_id"],
        note=decision["memo"],
    )`,
  go: `package main

import (
\t"context"
\t"errors"

\tmelio "github.com/melio/melio-go"
)

var approvedVendors = map[string]bool{
\t"acct_7c4b1": true,
\t"acct_5p2q9": true,
}

const spendCap = 500_000

func agentPay(client *melio.Client, d AgentDecision) error {
\tif !approvedVendors[d.VendorID] {
\t\treturn errors.New("vendor not approved")
\t}
\tif d.Amount > spendCap {
\t\treturn errors.New("exceeds spend cap")
\t}
\t_, err := client.Payments.Create(context.Background(), d.BusinessID, &melio.PaymentParams{
\t\tAmount:           melio.Int(d.Amount),
\t\tOriginatingAccountID:  melio.String("acct_9a2f7"),
\t\tReceivingAccountID: melio.String(d.VendorID),
\t\tDeductionDate:    melio.String(d.Date),
\t\tIdempotencyKey:   melio.String(d.TaskID),
\t\tNote:             melio.String(d.Memo),
\t})
\treturn err
}`,
};

const USE_CASES: UseCase[] = [
  {
    id: 'mass',
    icon: 'list',
    title: 'Mass payout',
    tagline: 'One payer pays thousands of recipients at once — like 5,000 checks on the 1st of every month.',
    summary:
      'Pay a whole list from a single business and funding source. There is no bulk endpoint to learn — you loop the same create-payment call, one per recipient, and a per-row idempotency key makes the entire run safe to retry. Melio mails paper checks on your behalf.',
    tags: [
      { kind: 'brand', text: '1 payer → many' },
      { kind: 'neutral', text: 'Batch · Paper check' },
    ],
    flow: {
      height: 214,
      columns: [
        [{ tone: 'payer', icon: 'business', label: 'Acme Insurance', sub: 'acct_9a2f7' }],
        [
          { tone: 'recipient', icon: 'contacts', label: 'Dana R.' },
          { tone: 'recipient', icon: 'contacts', label: 'Marco P.' },
          { tone: 'recipient', icon: 'contacts', label: '+4,997 insurees' },
        ],
      ],
      edges: [
        { from: [0, 0], to: [1, 0] },
        { from: [0, 0], to: [1, 1] },
        { from: [0, 0], to: [1, 2] },
      ],
    },
    steps: [
      {
        method: 'POST',
        path: '/accounts',
        title: 'Register each recipient once',
        desc: 'Payees persist — ACH or paper check. You only create them the first time they appear.',
      },
      {
        method: 'LOOP',
        path: '/payments',
        title: 'One payment per recipient',
        desc: 'Iterate your batch; each row is an independent, individually-tracked payment.',
      },
      {
        method: 'POST',
        path: 'Idempotency-Key',
        title: 'Make the batch replay-safe',
        desc: 'The Idempotency-Key header is required on payment creation; a unique key per row means a retried run never double-pays anyone.',
      },
    ],
    goodFor: ['Insurance claim disbursements', 'Payroll & contractor runs', 'Supplier settlement batches'],
    code: MASS_CODE,
    response: `{
  "scheduled": 3,
  "totalAmount": 697000,
  "payments": [
    { "id": "pay_a1f4", "receivingAccountId": "acct_7c4b1", "amount": 480000, "status": "scheduled" },
    { "id": "pay_b2c9", "receivingAccountId": "acct_5p2q9", "amount": 125000, "status": "scheduled" },
    { "id": "pay_c3e7", "receivingAccountId": "acct_3a8w2", "amount":  92000, "status": "scheduled" }
  ]
}`,
  },
  {
    id: 'platform',
    icon: 'buildings',
    title: 'Platform payouts',
    tagline: 'A platform pays on behalf of the many businesses it manages.',
    summary:
      'Your platform is the Melio customer: with a trusted partner key it manages many businesses and moves money on their behalf. Each managed business pays from its own funding source to its own recipients — funds never route through the platform. The business id in every path scopes the call, so the same code serves one managed business or thousands.',
    tags: [
      { kind: 'brand', text: 'many payers → many' },
      { kind: 'neutral', text: 'Multi-entity' },
    ],
    fromLabel: 'Managed businesses',
    toLabel: 'Recipients',
    flow: {
      height: 252,
      wrap: { col: 0, label: 'Platform' },
      columns: [
        [
          { tone: 'payer', icon: 'business', label: 'Sunrise Bakery' },
          { tone: 'payer', icon: 'business', label: 'Cedar & Co.' },
          { tone: 'payer', icon: 'business', label: 'Bright Dental' },
        ],
        [
          { tone: 'recipient', icon: 'contacts', label: 'Flour Power Co.' },
          { tone: 'recipient', icon: 'contacts', label: 'Westfield Mgmt' },
          { tone: 'recipient', icon: 'contacts', label: 'Bay Area Linen' },
        ],
      ],
      edges: [
        { from: [0, 0], to: [1, 0] },
        { from: [0, 0], to: [1, 1] },
        { from: [0, 1], to: [1, 1] },
        { from: [0, 1], to: [1, 2] },
        { from: [0, 2], to: [1, 0] },
        { from: [0, 2], to: [1, 2] },
      ],
    },
    steps: [
      {
        method: 'GET',
        path: '/businesses',
        title: 'List the businesses you manage',
        desc: "Your platform's trusted partner key can onboard and act on behalf of every business under it.",
      },
      {
        method: 'GET',
        path: '/accounts',
        title: "Use each business's own source",
        desc: "The platform pays from the managed business's funding source — money never routes through the platform itself.",
      },
      {
        method: 'LOOP',
        path: '/payments',
        title: 'Pay on their behalf',
        desc: 'For each managed business, create a payment to each of its recipients.',
      },
    ],
    goodFor: ['Marketplaces & vendor networks', 'AP platforms paying for many clients', 'Franchise / multi-entity finance teams'],
    code: M2M_CODE,
    response: `{
  "businesses": 3,
  "payments": 6,
  "totalAmount": 2148000,
  "byBusiness": [
    { "businessId": "ent_9688713", "payments": 2, "amount": 605000 },
    { "businessId": "ent_2k9p4x", "payments": 2, "amount": 1010000 },
    { "businessId": "ent_5q3n6t", "payments": 2, "amount": 533000 }
  ]
}`,
  },
  {
    id: 'recurring',
    icon: 'calendar',
    title: 'Recurring payouts',
    tagline: 'Pay the same recipients on a repeating schedule — each by their preferred delivery method.',
    summary:
      'Schedule the same payments to fire every billing cycle. There is no recurring object to manage — you call the same payments endpoint each period and use a period-scoped idempotency key to ensure retries never double-pay. Each recipient can use a different rail: check, card, or ACH.',
    tags: [
      { kind: 'brand', text: '1 payer → many' },
      { kind: 'neutral', text: 'Recurring · Scheduled' },
    ],
    flow: {
      height: 230,
      columns: [
        [{ tone: 'payer', icon: 'business', label: 'Sunrise Bakery', sub: 'monthly' }],
        [
          { tone: 'recipient', icon: 'contacts', label: 'Flour Power Co.', sub: 'via check' },
          { tone: 'recipient', icon: 'contacts', label: 'Bay Area Linen', sub: 'via card' },
          { tone: 'recipient', icon: 'contacts', label: 'Acme Packaging', sub: 'via ACH' },
        ],
      ],
      edges: [
        { from: [0, 0], to: [1, 0] },
        { from: [0, 0], to: [1, 1] },
        { from: [0, 0], to: [1, 2] },
      ],
    },
    steps: [
      {
        method: 'POST',
        path: '/accounts',
        title: 'Register each recipient with their preferred rail',
        desc: 'Create ACH, paper check, or virtual card delivery methods — once per recipient, reused every cycle.',
      },
      {
        method: 'LOOP',
        path: '/payments',
        title: 'Re-run the schedule each period',
        desc: 'Call the same payments endpoint on your billing cycle. No recurring object to manage.',
      },
      {
        method: 'POST',
        path: 'Idempotency-Key',
        title: 'Scope keys to the period',
        desc: 'The Idempotency-Key header is required on payment creation; use a period-prefixed key (e.g. "2026-07:rent") so any retry within the cycle is safe.',
      },
    ],
    goodFor: ['Monthly rent & utility payments', 'Recurring contractor stipends', 'Subscription-based supplier agreements'],
    code: RECURRING_CODE,
    response: `{
  "scheduled": 3,
  "period": "2026-07",
  "payments": [
    { "id": "pay_r1a2", "receivingAccountId": "acct_7c4b1", "amount": 125000, "status": "scheduled", "deliveryPreference": "standard-check" },
    { "id": "pay_r2b3", "receivingAccountId": "acct_5p2q9", "amount":  92000, "status": "scheduled", "deliveryPreference": "virtual-card" },
    { "id": "pay_r3c4", "receivingAccountId": "acct_3a8w2", "amount":  54200, "status": "scheduled", "deliveryPreference": "standard-ach" }
  ]
}`,
  },
  {
    id: 'agent',
    icon: 'fast',
    title: 'Agent-initiated payouts',
    tagline: 'An AI agent creates and runs payouts through the same API — with the guardrails you set.',
    summary:
      'AI agents use the exact same payments API your app uses — no special mode required. You enforce guardrails in your code (approved vendor list, spend cap, human-in-the-loop approval) before the agent can call the API. The agent gets the same trackable payment objects back.',
    tags: [
      { kind: 'brand', text: 'agent → many' },
      { kind: 'neutral', text: 'MCP · Guardrails' },
    ],
    flow: {
      height: 180,
      columns: [
        [{ tone: 'payer', icon: 'fast', label: 'Sunrise Bakery', sub: 'AI agent' }],
        [
          { tone: 'recipient', icon: 'contacts', label: 'Flour Power Co.' },
          { tone: 'recipient', icon: 'contacts', label: 'Bay Area Linen' },
        ],
      ],
      edges: [
        { from: [0, 0], to: [1, 0], label: 'via MCP' },
        { from: [0, 0], to: [1, 1], label: 'via MCP' },
      ],
    },
    steps: [
      {
        method: 'GET',
        path: '/accounts',
        title: 'Build the approved vendor list',
        desc: 'Fetch and cache the delivery methods the agent is allowed to pay. Everything else is blocked.',
      },
      {
        method: 'POST',
        path: '/payments',
        title: 'Agent calls the same endpoint',
        desc: 'No special mode — the agent uses the same payments API with guardrails checked in your wrapper.',
      },
      {
        method: 'GET',
        path: '/payments/{id}',
        title: 'Track the outcome',
        desc: 'Poll or subscribe to webhooks; the agent receives the same lifecycle events as any other payment.',
      },
    ],
    goodFor: ['AI-driven AP automation', 'Agentic expense reimbursement', 'Autonomous invoice processing with human approval gates'],
    code: AGENT_CODE,
    response: `{
  "id": "pay_ag1x2y",
  "amount": 125000,
  "currency": "USD",
  "status": "scheduled",
  "originatingAccountId": "acct_9a2f7",
  "receivingAccountId": "acct_7c4b1",
  "deductionDate": "2026-07-10",
  "deliveryDate": "2026-07-14",
  "note": "Flour order — approved by AP agent",
  "createdAt": "2026-07-08T14:22:11Z"
}`,
  },
];

// ---- Overview row ----------------------------------------------------------
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 8 }}>
      {children}
    </div>
  );
}

function UseCaseRow({ uc, onOpen }: { uc: UseCase; onOpen: () => void }) {
  const cf = uc.cardFlow || uc.flow;
  const fan = cf.columns.some((c) => c.length > 1) || cf.columns.length > 2;
  return (
    <div className="uc-card" onClick={onOpen}>
      <div className="uc-row">
        <div
          style={{
            background: 'var(--neutral-200)',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            borderRight: '1px solid var(--border-static)',
          }}
        >
          <div style={{ width: '100%' }}>
            <FlowDiagram columns={cf.columns} edges={cf.edges} height={fan ? 192 : 124} compact wrap={cf.wrap} />
          </div>
        </div>
        <div style={{ padding: '26px 30px', display: 'flex', flexDirection: 'column', gap: 11, justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: 'var(--accent-soft)',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={uc.icon} size={20} color="var(--accent)" />
            </span>
            <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-0.01em' }}>{uc.title}</div>
          </div>
          <div style={{ fontSize: 14.5, lineHeight: '22px', color: 'var(--text-secondary)' }}>{uc.tagline}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            {uc.tags.map((t, i) => (
              <Pill key={i} kind={t.kind as 'brand' | 'neutral'}>
                {t.text}
              </Pill>
            ))}
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--accent-text)',
              fontSize: 13.5,
              fontWeight: 600,
              marginTop: 6,
            }}
          >
            See how it works
            <Icon name="arrow-right" size={15} color="var(--accent-text)" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Overview --------------------------------------------------------------
const RAILS = ['ACH', 'Card', 'Wire', 'Virtual card', 'Paper check', 'International'];

function Overview({ onOpen }: { onOpen: (id: string) => void }) {
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 28px' }}>
      <section style={{ padding: '52px 0 28px', maxWidth: 720, textAlign: 'center', margin: '0 auto' }}>
        <Eyebrow>Use cases</Eyebrow>
        <h1 className="display-2" style={{ margin: 0, fontSize: 44, lineHeight: '52px', letterSpacing: '-0.02em' }}>
          However money needs to move
        </h1>
        <p style={{ fontSize: 18, lineHeight: '28px', color: 'var(--text-secondary)', margin: '18px 0 0' }}>
          One payment or millions, one business or a whole platform, kicked off by your code or an AI agent — the same payouts API covers
          it.
        </p>
      </section>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
          margin: '28px 0 44px',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginRight: 2 }}>
          Every use case runs on any rail:
        </span>
        {RAILS.map((r) => (
          <span
            key={r}
            style={{
              background: 'var(--neutral-100)',
              border: '1px solid var(--border-static)',
              borderRadius: 999,
              padding: '5px 13px',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text-primary)',
            }}
          >
            {r}
          </span>
        ))}
      </div>

      <section style={{ paddingBottom: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {USE_CASES.map((uc) => (
            <UseCaseRow key={uc.id} uc={uc} onOpen={() => onOpen(uc.id)} />
          ))}
        </div>
      </section>

      <section style={{ padding: '8px 0 64px' }}>
        <Card
          padding={0}
          style={{ overflow: 'hidden', border: '1px solid rgba(33,33,36,0.05)', boxShadow: '0 8px 24px rgba(33,33,36,0.07)' }}
        >
          <div style={{ padding: '18px 22px 4px' }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>The same four building blocks</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 3 }}>
              Every use case above is composed from these resources — in the order you call them.
            </div>
          </div>
          <div className="grid-4" style={{ gap: 0, padding: '14px 8px 18px' }}>
            {(
              [
                ['business', 'Business', 'The entity that pays. KYC gates eligibility.'],
                ['bank', 'Funding source', 'The bank account you debit.'],
                ['contacts', 'Delivery method', 'Where money lands — ACH, card, or check.'],
                ['dollar-fill', 'Payment', 'Moves the money; tracked through its lifecycle.'],
              ] as [string, string, string][]
            ).map(([ic, t, d], i) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', padding: '0 14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: '6px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <Icon name={ic} size={20} color="var(--accent)" />
                    <span style={{ fontSize: 14.5, fontWeight: 600 }}>{t}</span>
                  </div>
                  <div style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>{d}</div>
                </div>
                {i < 3 && <Icon name="arrow-right" size={16} color="var(--neutral-500)" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}

// ---- Detail ----------------------------------------------------------------
function StepItem({ s, last }: { s: UseCase['steps'][0] & { _n: number }; last: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--accent-soft)',
            border: '1.5px solid var(--accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-text)' }}>{s._n}</span>
        </div>
        {!last && <div style={{ width: 2, flex: 1, minHeight: 20, background: 'var(--border-static)', margin: '4px 0' }} />}
      </div>
      <div style={{ paddingBottom: last ? 0 : 22, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{s.title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
          <Method m={s.method} />
          <code className="om-mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {s.path}
          </code>
        </div>
        <div style={{ fontSize: 13.5, lineHeight: '20px', color: 'var(--text-secondary)' }}>{s.desc}</div>
      </div>
    </div>
  );
}

function Detail({ uc, onBack, go }: { uc: UseCase; onBack: () => void; go: Go }) {
  const [showResp, setShowResp] = useState(false);

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 28px 72px' }}>
      <div style={{ padding: '24px 0 4px' }}>
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            fontSize: 13.5,
            fontWeight: 600,
            padding: '4px 0',
          }}
        >
          <Icon name="arrow-left" size={15} color="var(--text-secondary)" />
          All use cases
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 14, flexWrap: 'wrap' }}>
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 11,
            background: 'var(--accent-soft)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={uc.icon} size={24} color="var(--accent)" />
        </span>
        <h1 style={{ margin: 0, fontSize: 32, letterSpacing: '-0.02em' }}>{uc.title}</h1>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {uc.tags.map((t, i) => (
            <Pill key={i} kind={t.kind as 'brand' | 'neutral'}>
              {t.text}
            </Pill>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 16.5, lineHeight: '26px', color: 'var(--text-secondary)', margin: '0 0 24px', maxWidth: 760 }}>{uc.summary}</p>

      <div
        style={{
          background: 'var(--neutral-200)',
          border: '1px solid rgba(33,33,36,0.05)',
          borderRadius: 16,
          boxShadow: '0 6px 22px rgba(33,33,36,0.06)',
          padding: '20px 28px',
          marginBottom: 34,
        }}
      >
        {(uc.fromLabel || uc.toLabel) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 880, margin: '0 auto 2px' }}>
            <span className="eyebrow" style={{ color: 'var(--text-secondary)' }}>
              {uc.fromLabel || 'From'}
            </span>
            <span className="eyebrow" style={{ color: 'var(--text-secondary)' }}>
              {uc.toLabel || 'To'}
            </span>
          </div>
        )}
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <FlowDiagram columns={uc.flow.columns} edges={uc.flow.edges} height={uc.flow.height} wrap={uc.flow.wrap} />
        </div>
      </div>

      <div className="doc-cols">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 18, letterSpacing: '-0.01em' }}>How it works</div>
          <div>
            {uc.steps.map((s, i) => (
              <StepItem key={i} s={{ ...s, _n: i + 1 }} last={i === uc.steps.length - 1} />
            ))}
          </div>

          <div style={{ marginTop: 30, fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Good for</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {uc.goodFor.map((g, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, color: 'var(--text-primary)' }}>
                <Icon name="checked-circle-fill" size={17} color="var(--success-700)" />
                {g}
              </div>
            ))}
          </div>
        </div>

        <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Icon name="document" size={16} color="var(--icon-primary)" />
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>Example</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>· @melio/sdk</span>
          </div>
          <CodeBlock langs={uc.code} dark maxHeight={460} />

          <div style={{ marginTop: 14 }}>
            <button
              onClick={() => setShowResp((c) => !c)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                marginBottom: showResp ? 8 : 0,
              }}
            >
              <Icon name={showResp ? 'chevron-down' : 'chevron-right'} size={12} color="var(--text-secondary)" />
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '.04em',
                }}
              >
                Example response · 201 Created
              </span>
            </button>
            {showResp && <CodeBlock code={uc.response} dark maxHeight={320} />}
          </div>

          <div
            style={{
              marginTop: 16,
              padding: '12px 14px',
              background: 'var(--accent-soft)',
              borderRadius: 10,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <Icon name="fast" size={16} color="var(--accent)" style={{ marginTop: 1 }} />
            <div style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--accent-text)' }}>
              Try this live in the <strong>API reference</strong> — every call here has a "Send request" console.
              <button
                onClick={() => go('/reference')}
                style={{
                  marginLeft: 4,
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-text)',
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                Open reference →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Page ------------------------------------------------------------------
export function UseCasesHub() {
  const go = useGo();
  const { id } = useParams<{ id?: string }>();
  const uc = USE_CASES.find((u) => u.id === id) ?? null;

  return uc ? <Detail uc={uc} onBack={() => go('/use-cases')} go={go} /> : <Overview onOpen={(ucId) => go(`/use-cases/${ucId}`)} />;
}
